if (!customElements.get('delivery-estimator')) {
  class DeliveryEstimator extends HTMLElement {
    // Approximate centroids for nearest-city matching (no external API needed)
    static CENTROIDS = {
      Dubai:            { lat: 25.2048, lng: 55.2708 },
      Sharjah:          { lat: 25.3463, lng: 55.4209 },
      'Abu Dhabi':      { lat: 24.4539, lng: 54.3773 },
      Ajman:            { lat: 25.4052, lng: 55.5136 },
      'Ras Al Khaimah': { lat: 25.8007, lng: 55.9762 },
      Fujairah:         { lat: 25.1288, lng: 56.3265 },
      'Umm Al Quwain':  { lat: 25.5641, lng: 55.5553 },
      'Al Ain':         { lat: 24.2075, lng: 55.7447 },
    };

    static STORAGE_KEY = 'de_city';

    constructor() {
      super();
      this._city = 'Dubai';
      this._refreshTimer = null;
      this._onChangeClick      = this._onChangeClick.bind(this);
      this._onCityClick        = this._onCityClick.bind(this);
      this._onDropdownClick    = this._onDropdownClick.bind(this);
      this._onModalCloseClick  = this._onModalCloseClick.bind(this);
      this._onLocateClick      = this._onLocateClick.bind(this);
      this._onKeydown          = this._onKeydown.bind(this);
      this._onDocClick         = this._onDocClick.bind(this);
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
    }

    connectedCallback() {
      // Priority: localStorage > IP geolocation (async) > customer address > Dubai
      const stored = localStorage.getItem(DeliveryEstimator.STORAGE_KEY);
      this._city = stored || this.dataset.defaultCity || 'Dubai';

      const changeBtn = this.querySelector('[data-change-btn]');
      const dropdown  = this.querySelector('[data-city-dropdown]');
      const closeBtn  = this.querySelector('[data-modal-close]');
      const locateBtn = this.querySelector('[data-locate-btn]');

      changeBtn.addEventListener('click', this._onChangeClick);
      dropdown.addEventListener('click', this._onCityClick);
      dropdown.addEventListener('click', this._onDropdownClick);
      if (closeBtn)  closeBtn.addEventListener('click', this._onModalCloseClick);
      if (locateBtn) locateBtn.addEventListener('click', this._onLocateClick);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKeydown);
      document.addEventListener('visibilitychange', this._onVisibilityChange);

      this._render();
      this._scheduleRefresh();

      // Auto-detect city by IP on first visit (no permission prompt)
      if (!stored) this._detectIPLocation();
    }

    disconnectedCallback() {
      const changeBtn = this.querySelector('[data-change-btn]');
      const dropdown  = this.querySelector('[data-city-dropdown]');
      const closeBtn  = this.querySelector('[data-modal-close]');
      const locateBtn = this.querySelector('[data-locate-btn]');

      if (changeBtn)  changeBtn.removeEventListener('click', this._onChangeClick);
      if (dropdown)   dropdown.removeEventListener('click', this._onCityClick);
      if (dropdown)   dropdown.removeEventListener('click', this._onDropdownClick);
      if (closeBtn)   closeBtn.removeEventListener('click', this._onModalCloseClick);
      if (locateBtn)  locateBtn.removeEventListener('click', this._onLocateClick);
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      document.body.style.overflow = '';
      clearTimeout(this._refreshTimer);
      clearTimeout(this._msgTimer);
    }

    // ── Event handlers ──────────────────────────────────────────────────────

    _onChangeClick(evt) {
      evt.stopPropagation();
      const dropdown = this.querySelector('[data-city-dropdown]');
      const btn      = this.querySelector('[data-change-btn]');
      const isHidden = dropdown.hasAttribute('hidden');
      if (isHidden) {
        dropdown.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
      } else {
        dropdown.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }
    }

    _onDropdownClick(evt) {
      const modal = this.querySelector('.delivery-estimator__modal');
      if (modal && !modal.contains(evt.target)) {
        this._closeDropdown();
        return;
      }
      evt.stopPropagation();
    }

    _onModalCloseClick(evt) {
      evt.stopPropagation();
      this._closeDropdown();
    }

    _onKeydown(evt) {
      if (evt.key === 'Escape') this._closeDropdown();
    }

    _onCityClick(evt) {
      const item = evt.target.closest('[data-city]');
      if (!item) return;
      this._city = item.dataset.city;
      localStorage.setItem(DeliveryEstimator.STORAGE_KEY, this._city);
      this._closeDropdown();
      this._syncCityUI();
      this._render();
      this._dispatchCityChange(this._city);
    }

    _onDocClick(evt) {
      if (!this.contains(evt.target)) this._closeDropdown();
    }

    _onLocateClick(evt) {
      evt.stopPropagation();
      this._detectLocation();
    }

    _onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      this._render();
      this._scheduleRefresh();
    }

    // ── UI helpers ──────────────────────────────────────────────────────────

    _closeDropdown() {
      const dropdown = this.querySelector('[data-city-dropdown]');
      if (dropdown && !dropdown.hasAttribute('hidden')) {
        dropdown.setAttribute('hidden', '');
        this.querySelector('[data-change-btn]').setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      }
    }

    _syncCityUI() {
      const nameEl = this.querySelector('[data-city-name]');
      if (nameEl) nameEl.textContent = this._city;

      this.querySelectorAll('[data-city]').forEach((item) => {
        const active = item.dataset.city === this._city;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
      });
    }

    // ── Geolocation ─────────────────────────────────────────────────────────

    _nearestCity(lat, lng) {
      let nearest = 'Dubai';
      let minDist = Infinity;
      for (const [city, c] of Object.entries(DeliveryEstimator.CENTROIDS)) {
        const d = Math.hypot(lat - c.lat, lng - c.lng);
        if (d < minDist) { minDist = d; nearest = city; }
      }
      return nearest;
    }

    _detectLocation() {
      if (!navigator.geolocation) {
        this._showGeoMessage('Unable to detect your location. Please select your city manually.');
        return;
      }

      const btn   = this.querySelector('[data-locate-btn]');
      const label = this.querySelector('[data-locate-label]');

      if (btn)   btn.disabled = true;
      if (label) label.textContent = 'Detecting…';

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude: lat, longitude: lng } = pos.coords;

          // Requirement 4 & 5: validate UAE bounds before proceeding
          const isInUAE = lat >= 22.5 && lat <= 26.5 && lng >= 51.5 && lng <= 56.5;
          if (!isInUAE) {
            if (btn)   btn.disabled = false;
            if (label) label.textContent = 'Use my location';
            this._showGeoMessage(
              'Delivery estimation is available only for UAE locations. Please select a UAE city manually.'
            );
            return;
          }

          // Inside UAE: update city, persist, refresh UI
          const city = this._nearestCity(lat, lng);
          this._city = city;
          localStorage.setItem(DeliveryEstimator.STORAGE_KEY, city);
          this._closeDropdown();
          this._syncCityUI();
          this._render();
          this._dispatchCityChange(city);
        },
        (err) => {
          if (btn)   btn.disabled = false;
          if (label) label.textContent = 'Use my location';
          if (err.code === 1) {
            // PERMISSION_DENIED
            this._showGeoMessage('Location access was denied. Please select your city manually.');
          } else {
            // POSITION_UNAVAILABLE or TIMEOUT
            this._showGeoMessage('Unable to detect your location. Please select your city manually.');
          }
        },
        { timeout: 8000 }
      );
    }

    _dispatchCityChange(city) {
      window.dispatchEvent(new CustomEvent('delivery-city-changed', { detail: { city } }));
    }

    _showGeoMessage(text) {
      const el = this.querySelector('[data-geo-message]');
      if (!el) return;
      el.textContent = text;
      el.removeAttribute('hidden');
      clearTimeout(this._msgTimer);
      this._msgTimer = setTimeout(() => el.setAttribute('hidden', ''), 5000);
    }

    _detectIPLocation() {
      fetch('https://ipapi.co/json/')
        .then((r) => r.json())
        .then((data) => {
          // Non-UAE visitors: keep Dubai default, do not touch localStorage
          if (data.country_code !== 'AE') return;
          const city = this._matchCity(data.city || '');
          if (!city) return;
          this._city = city;
          localStorage.setItem(DeliveryEstimator.STORAGE_KEY, city);
          this._syncCityUI();
          this._render();
          this._dispatchCityChange(city);
        })
        .catch(() => {}); // silently ignore — Dubai default stays
    }

    // Fuzzy-match an arbitrary city string to our known UAE cities
    _matchCity(raw) {
      const s = raw.toLowerCase().trim();
      const map = {
        dubai:            'Dubai',
        sharjah:          'Sharjah',
        'abu dhabi':      'Abu Dhabi',
        abudhabi:         'Abu Dhabi',
        ajman:            'Ajman',
        'ras al khaimah': 'Ras Al Khaimah',
        'ras-al-khaimah': 'Ras Al Khaimah',
        rak:              'Ras Al Khaimah',
        fujairah:         'Fujairah',
        'umm al quwain':  'Umm Al Quwain',
        'umm-al-quwain':  'Umm Al Quwain',
        uaq:              'Umm Al Quwain',
        'al ain':         'Al Ain',
        'al-ain':         'Al Ain',
        alain:            'Al Ain',
      };
      return map[s] || null;
    }

    // ── Render ───────────────────────────────────────────────────────────────

    _scheduleRefresh() {
      clearTimeout(this._refreshTimer);
      const { nextUpdateMs } = window.AEDeliveryDate.compute();
      const delay = Math.max(nextUpdateMs - Date.now() + 50, 50);
      this._refreshTimer = setTimeout(() => {
        this._render();
        this._scheduleRefresh();
      }, delay);
    }

    _render() {
      const dateEl  = this.querySelector('[data-delivery-date]');
      const labelEl = this.querySelector('[data-tier-label]');
      const result  = window.AEDeliveryDate.compute();

      this._syncCityUI();

      if (dateEl)  dateEl.textContent  = window.AEDeliveryDate.format(result, true);
      if (labelEl) labelEl.textContent = 'Estimated Delivery';
    }
  }

  customElements.define('delivery-estimator', DeliveryEstimator);
}
